import React, { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../contexts/AuthContext';
import { useSala } from '../contexts/SalaContext';
import logo from '../assets/logo-centrao.png';

// Modulos agrupados por DOMINIO. Cada modulo concentra operacao + admin do
// proprio dominio. "Administracao" sobra apenas para meta-config do portal
// (usuarios e perfis).
const MODULES = [
  {
    key: 'dashboard',
    standalone: true, // Renderizado como link flat (sem accordion)
    items: [
      {
        to: '/ti/dashboard',
        label: 'Dashboard',
        icon: 'bi-grid-1x2-fill',
        description: 'Visao geral da operacao de TI',
        permissao: 'ti:dashboard',
      },
    ],
  },
  {
    key: 'monitor-uso',
    standalone: true,
    items: [
      {
        to: '/monitor/uso-sistemas',
        label: 'Uso dos Sistemas',
        icon: 'bi-broadcast',
        description: 'Quem esta interagindo com cada sistema interno agora',
        permissao: 'monitor:visualizar',
      },
    ],
  },
  {
    key: 'apresentacoes',
    standalone: true,
    items: [
      {
        to: '/apresentacoes',
        label: 'Apresentacoes de Resultados',
        icon: 'bi-easel',
        description: 'Relatorios executivos das entregas da TI',
      },
    ],
  },
  {
    key: 'analise-banco',
    standalone: true,
    items: [
      {
        to: '/ti/analise-banco',
        label: 'Análise do Banco de Dados',
        icon: 'bi-database-gear',
        description: 'O que está pesando no SQL Server e de quem é',
        permissao: 'ti:analise-banco',
      },
    ],
  },
  {
    key: 'vendas-web',
    standalone: true,
    items: [
      {
        to: '/ti/vendas-web',
        label: 'Adoção Vendas Web',
        icon: 'bi-graph-up-arrow',
        description: 'Quem já migrou para a plataforma nova de vendas',
        permissao: 'ti:vendas-web',
      },
    ],
  },
  {
    key: 'avisos',
    standalone: true,
    items: [
      {
        to: '/avisos',
        label: 'Avisos',
        icon: 'bi-megaphone',
        description: 'Avisos da diretoria sobre a operação e os sistemas',
        permissao: 'diretoria:avisos',
      },
    ],
  },
  {
    key: 'sala',
    standalone: true,
    items: [
      {
        to: '/ti/sala',
        label: 'Sala de Voz',
        icon: 'bi-camera-video-fill',
        description: 'Chamada de voz e vídeo interna da equipe de TI',
        permissao: 'ti:sala',
      },
    ],
  },
  {
    key: 'cameras',
    standalone: true,
    items: [
      {
        to: '/ti/cameras',
        label: 'Câmeras',
        icon: 'bi-webcam-fill',
        description: 'Tour ao vivo das câmeras dos DVRs e NVRs',
        permissao: 'ti:cameras',
      },
    ],
  },
  {
    key: 'checklist',
    standalone: true,
    items: [
      {
        to: '/ti/checklist',
        label: 'Checklist',
        icon: 'bi-check2-square',
        description: 'Checkpoints diarios e semanais da equipe de TI',
        permissao: 'ti:checklist',
      },
    ],
  },
  {
    key: 'atendimento',
    label: 'Atendimento',
    icon: 'bi-chat-dots',
    items: [
      {
        to: '/ti/atendimentos',
        label: 'Conversas',
        icon: 'bi-chat-left-text',
        description: 'Conversas WhatsApp dos usuarios',
        permissao: 'ti:atendimentos',
      },
      {
        to: '/ti/chamados',
        label: 'Chamados',
        icon: 'bi-headset',
        description: 'Atendimento de solicitacoes e incidentes',
        permissao: 'ti:chamados',
        permissaoAlternativa: 'ti:chamados:abrir',
      },
      {
        to: '/ti/atendimentos/indicadores',
        label: 'Indicadores',
        icon: 'bi-graph-up',
        description: 'KPIs de atendimento, TTFR/TTR e SLA por periodo',
        permissao: 'ti:atendimentos',
        permissaoAlternativa: 'admin:atendimento',
      },
      {
        to: '/ti/contatos',
        label: 'Contatos',
        icon: 'bi-person-vcard',
        description: 'Cadastro de contatos para iniciar conversas outbound',
        permissao: 'ti:atendimentos',
      },
      {
        to: '/admin/whatsapp',
        label: 'Canais WhatsApp',
        icon: 'bi-whatsapp',
        description: 'Canais de atendimento (numeros conectados)',
        permissao: 'admin:whatsapp',
      },
      {
        to: '/admin/fluxos',
        label: 'Fluxos do Bot',
        icon: 'bi-diagram-3',
        description: 'Construtor de fluxos de atendimento por canal',
        permissao: 'admin:fluxos',
      },
      {
        to: '/admin/atendimento',
        label: 'Configuracoes',
        icon: 'bi-sliders',
        description: 'Horario, mensagens, SLA, catalogos',
        permissao: 'admin:atendimento',
      },
    ],
  },
  {
    key: 'equipamentos',
    label: 'Equipamentos',
    icon: 'bi-laptop',
    items: [
      {
        to: '/ti/equipamentos',
        label: 'Equipamentos',
        icon: 'bi-laptop',
        description: 'Celulares, tablets, notebooks e demais ativos (base compartilhada com sistema-suc)',
        permissao: 'ti:equipamentos',
      },
      {
        to: '/ti/solicitacoes',
        label: 'Solicitacoes',
        icon: 'bi-inbox-fill',
        description: 'Solicitacoes de equipamentos novos ou trocas',
        permissao: 'ti:equipamentos',
        permissaoAlternativa: 'ti:solicitacoes',
      },
      {
        to: '/ti/agentes',
        label: 'Agentes',
        icon: 'bi-hdd-network-fill',
        description: 'Maquinas com agente instalado e conectadas ao portal',
        permissao: 'ti:equipamentos',
      },
      {
        to: '/ti/topologia',
        label: 'Topologia',
        icon: 'bi-diagram-3-fill',
        description: 'Mapa visual da infraestrutura (servidores, redes, servicos)',
        permissao: 'ti:topologia',
      },
      {
        to: '/ti/monitoramento',
        label: 'Monitoramento',
        icon: 'bi-activity',
        description: 'CPU, memoria, disco e alertas dos servidores (Prometheus)',
        permissao: 'ti:monitoramento',
      },
    ],
  },
  {
    key: 'insumos',
    label: 'Insumos',
    icon: 'bi-box-seam',
    items: [
      {
        to: '/ti/insumos',
        label: 'Insumos',
        icon: 'bi-box-seam',
        description: 'Estoque de papel, toner e suprimentos',
        permissao: 'ti:insumos',
      },
    ],
  },
  {
    key: 'martelinho',
    label: 'Martelinho',
    icon: 'bi-hammer',
    items: [
      {
        to: '/martelinho/indicadores',
        label: 'Indicadores',
        icon: 'bi-speedometer2',
        description: 'Visao geral do bot do ERP',
        permissao: 'martelinho:visualizar',
        permissaoAlternativa: 'martelinho:gerenciar',
      },
      {
        to: '/martelinho/usuarios',
        label: 'Usuarios',
        icon: 'bi-people',
        description: 'Quem pode usar o bot e suas permissoes',
        permissao: 'martelinho:visualizar',
        permissaoAlternativa: 'martelinho:gerenciar',
      },
      {
        to: '/martelinho/historico',
        label: 'Historico',
        icon: 'bi-clock-history',
        description: 'Alteracoes feitas pelo bot',
        permissao: 'martelinho:visualizar',
        permissaoAlternativa: 'martelinho:gerenciar',
      },
      {
        to: '/martelinho/funcionalidades',
        label: 'Funcionalidades',
        icon: 'bi-list-check',
        description: 'O que cada opcao do menu do WhatsApp faz',
        permissao: 'martelinho:visualizar',
        permissaoAlternativa: 'martelinho:gerenciar',
      },
      {
        to: '/martelinho/conexao',
        label: 'Conexao',
        icon: 'bi-qr-code',
        description: 'Conectar o numero do bot (uma unica conexao)',
        permissao: 'martelinho:gerenciar',
      },
    ],
  },
  {
    key: 'admin',
    label: 'Administracao',
    icon: 'bi-gear',
    items: [
      {
        to: '/admin/usuarios',
        label: 'Usuarios',
        icon: 'bi-person-gear',
        description: 'Gerenciar usuarios do portal',
        permissao: 'admin:usuarios',
      },
      {
        to: '/admin/perfis',
        label: 'Perfis',
        icon: 'bi-shield-lock',
        description: 'Gerenciar perfis e permissoes',
        permissao: 'admin:perfis',
      },
      {
        to: '/admin/notificacoes',
        label: 'Notificacoes',
        icon: 'bi-bell',
        description: 'Alertas automaticos via WhatsApp (SEFAZ, etc.)',
        permissao: 'admin:notificacoes',
      },
      {
        to: '/admin/backup',
        label: 'Backup',
        icon: 'bi-cloud-arrow-up',
        description: 'Backups agendados (SMB -> Local ou Google Drive)',
        permissao: 'admin:backup',
      },
      {
        to: '/admin/plataformas',
        label: 'Plataformas do Hub',
        icon: 'bi-grid-3x3-gap',
        description: 'Cadastro das plataformas exibidas no Hub Centrao',
        permissao: 'admin:usuarios',
      },
    ],
  },
];

const ALL_ITEMS = MODULES.flatMap((m) => m.items);

export default function Sidebar() {
  const { user, logout, temPermissao } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Recolhimento fixo (persistido) + espiada temporaria no hover (peek).
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('sidebar-collapsed') === '1'; } catch { return false; }
  });
  const [peeking, setPeeking] = useState(false);
  const peekTimer = useRef(null);

  // Espelha o estado fixo numa classe do body para o .main-content reagir
  // (empurrar conteudo) sem precisar elevar o estado ate o App.
  useEffect(() => {
    document.body.classList.toggle('sidebar-collapsed', collapsed);
    return () => document.body.classList.remove('sidebar-collapsed');
  }, [collapsed]);

  const toggleCollapsed = () => {
    setPeeking(false);
    setCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem('sidebar-collapsed', next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };

  // Peek com delay de intencao: so expande temporariamente se o mouse ficar.
  const handlePeekEnter = () => {
    if (!collapsed) return;
    clearTimeout(peekTimer.current);
    peekTimer.current = setTimeout(() => setPeeking(true), 320);
  };
  const handlePeekLeave = () => {
    clearTimeout(peekTimer.current);
    setPeeking(false);
  };
  useEffect(() => () => clearTimeout(peekTimer.current), []);

  // Fecha o drawer ao navegar. No mobile o menu vive por cima do conteudo com
  // backdrop — depois de tocar num link, o usuario espera voltar a ver a
  // pagina, nao o menu aberto.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const [naoLidas, setNaoLidas] = useState(0);
  const [vencidos, setVencidos] = useState(0);
  const [solicitacoesPendentes, setSolicitacoesPendentes] = useState(0);
  const [checklistPendentes, setChecklistPendentes] = useState(0);

  // Estado + controles da chamada de voz. O card fica no topo da nav enquanto
  // o usuario esta em OUTRA rota (dentro de /ti/sala a propria pagina ja tem
  // os controles). Traz mudo, camera, tela, sair e "voltar" para nao precisar
  // de um painel flutuante separado. No modo rail (sidebar recolhida), so o
  // atalho de "voltar" fica visivel — os controles voltam quando o menu expande.
  const {
    status: statusSala,
    sala: salaAtiva,
    participantes: participantesSala,
    mudo,
    cameraDesligada,
    compartilhandoTela,
    streamLocal,
    alternarMudo,
    alternarCamera,
    alternarTela,
    sair: sairDaSala,
  } = useSala();
  const emChamada = statusSala === 'na-sala' && location.pathname !== '/ti/sala';
  const totalNaSala = (participantesSala?.length || 0) + 1;
  const temCamera = !!streamLocal?.getVideoTracks?.().length;

  const podeAtendimentos = temPermissao('ti:atendimentos');
  const podeChamados = temPermissao('ti:chamados');
  // Contador so aparece pra quem gerencia — o backend ja devolve 0 pra outros
  // perfis, mas checar aqui evita chamar o endpoint a toa.
  const podeGerirSolicitacoes = temPermissao('ti:equipamentos');
  const podeChecklist = temPermissao('ti:checklist');

  useEffect(() => {
    if (!podeAtendimentos) {
      setNaoLidas(0);
      return undefined;
    }
    let ativo = true;
    const buscar = async () => {
      try {
        const res = await axios.get(`${API}/atendimentos/nao-lidas/total`);
        if (ativo) setNaoLidas(res.data?.data?.total || 0);
      } catch (e) { /* silencioso - badge e nice-to-have */ }
    };
    buscar();
    const id = setInterval(buscar, 10000);
    return () => {
      ativo = false;
      clearInterval(id);
    };
  }, [podeAtendimentos]);

  // Contagem de chamados vencidos (do usuario + sem atendente) pro badge.
  useEffect(() => {
    if (!podeChamados) {
      setVencidos(0);
      return undefined;
    }
    let ativo = true;
    const buscar = async () => {
      try {
        const res = await axios.get(`${API}/chamados/vencidos`);
        if (ativo) setVencidos((res.data?.data || []).length);
      } catch (e) { /* silencioso - badge e nice-to-have */ }
    };
    buscar();
    const id = setInterval(buscar, 60000);
    return () => {
      ativo = false;
      clearInterval(id);
    };
  }, [podeChamados]);

  // Solicitacoes de equipamento pendentes. Menos volatil que conversas —
  // 60s e suficiente (mesma cadencia dos chamados vencidos).
  useEffect(() => {
    if (!podeGerirSolicitacoes) {
      setSolicitacoesPendentes(0);
      return undefined;
    }
    let ativo = true;
    const buscar = async () => {
      try {
        const res = await axios.get(`${API}/solicitacoes/pendentes/total`);
        if (ativo) setSolicitacoesPendentes(res.data?.data?.total || 0);
      } catch (e) { /* silencioso - badge e nice-to-have */ }
    };
    buscar();
    const id = setInterval(buscar, 60000);
    return () => {
      ativo = false;
      clearInterval(id);
    };
  }, [podeGerirSolicitacoes]);

  // Checklist do dia: badge fica ate o item ser marcado como concluido.
  // Poll de 60s — a lista muda pouco (so quando alguem marca).
  useEffect(() => {
    if (!podeChecklist) {
      setChecklistPendentes(0);
      return undefined;
    }
    let ativo = true;
    const buscar = async () => {
      try {
        const res = await axios.get(`${API}/checklist/pendentes/total`);
        if (ativo) setChecklistPendentes(res.data?.data?.total || 0);
      } catch (e) { /* silencioso - badge e nice-to-have */ }
    };
    buscar();
    const id = setInterval(buscar, 60000);
    return () => {
      ativo = false;
      clearInterval(id);
    };
  }, [podeChecklist]);

  const visibleModules = useMemo(
    () => MODULES.map((mod) => ({
      ...mod,
      items: mod.items.filter((item) => {
        const liberado = item.permissao && temPermissao(item.permissao);
        const alternativa = item.permissaoAlternativa && temPermissao(item.permissaoAlternativa);
        if (item.permissao && !liberado && !alternativa) return false;
        return true;
      }),
    })).filter((mod) => mod.items.length > 0),
    [user]
  );

  const currentSection = useMemo(
    () => ALL_ITEMS.find((item) => location.pathname.startsWith(item.to)),
    [location.pathname]
  );

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNavigate = () => {
    setMobileOpen(false);
  };

  return (
    <>
      <header className="mobile-header">
        <button type="button" onClick={() => setMobileOpen((prev) => !prev)} aria-label="Abrir menu">
          <i className={`bi ${mobileOpen ? 'bi-x-lg' : 'bi-list'}`} />
        </button>
        <img src={logo} alt="Centrao" className="sidebar-logo" />
        <div className="sidebar-title">
          Portal TI
          <span>{currentSection?.label || 'Painel TI'}</span>
        </div>
        {emChamada && (
          <button
            type="button"
            className="mobile-header__em-chamada"
            onClick={() => navigate('/ti/sala')}
            aria-label={`Voltar para ${salaAtiva?.nome || 'a chamada'}`}
            title={`Voltar para ${salaAtiva?.nome || 'a chamada'}`}
          >
            <i className="bi bi-telephone-fill" />
            <span className="mobile-header__em-chamada-dot" aria-hidden="true" />
          </button>
        )}
      </header>

      <div className={`sidebar-overlay ${mobileOpen ? 'open' : ''}`} onClick={() => setMobileOpen(false)} />

      <aside
        className={`sidebar sidebar--model ${mobileOpen ? 'open' : ''} ${collapsed && !peeking ? 'sidebar--collapsed' : ''}`}
        onMouseEnter={handlePeekEnter}
        onMouseLeave={handlePeekLeave}
      >
        <div className="sidebar-header">
          <img src={logo} alt="Centrao Materiais de Construcao" className="sidebar-logo" />
          <div className="sidebar-title">
            Portal TI
            <span>Centrao</span>
          </div>
        </div>

        <button
          type="button"
          className="sidebar-collapse-btn"
          onClick={toggleCollapsed}
          title={collapsed ? 'Expandir menu' : 'Recolher menu'}
          aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
        >
          <i className="bi bi-chevron-left" />
        </button>

        <div className="sidebar-nav">
          {emChamada && (
            <div
              className="sidebar-em-chamada"
              role="region"
              aria-label="Chamada em andamento"
            >
              {/* Cabecalho clicavel: o toque principal do card e voltar para a
                  sala. No modo rail (sidebar recolhida) so este bloco aparece
                  — os controles ficam escondidos por padrao ate o menu expandir. */}
              <button
                type="button"
                className="sidebar-em-chamada__voltar"
                onClick={() => { navigate('/ti/sala'); setMobileOpen(false); }}
                data-tooltip={`Voltar para ${salaAtiva?.nome || 'a chamada'}`}
                title={`Voltar para ${salaAtiva?.nome || 'a chamada'}`}
              >
                <span className="sidebar-nav__icon-wrap">
                  <i className="bi bi-telephone-fill" />
                  <span className="sidebar-em-chamada__dot" aria-hidden="true" />
                </span>
                <span className="nav-label sidebar-em-chamada__label">
                  <span>Voltar para a chamada</span>
                  <span className="sidebar-em-chamada__sub">
                    {salaAtiva?.nome || 'Sala de voz'} · {totalNaSala} {totalNaSala === 1 ? 'pessoa' : 'pessoas'}
                  </span>
                </span>
              </button>

              <div className="sidebar-em-chamada__controles">
                <button
                  type="button"
                  onClick={alternarMudo}
                  className={`sidebar-em-chamada__ctrl ${mudo ? 'sidebar-em-chamada__ctrl--danger' : ''}`}
                  title={mudo ? 'Ativar microfone' : 'Silenciar microfone'}
                  aria-pressed={mudo}
                  aria-label={mudo ? 'Ativar microfone' : 'Silenciar microfone'}
                >
                  <i className={`bi ${mudo ? 'bi-mic-mute-fill' : 'bi-mic-fill'}`} />
                </button>
                <button
                  type="button"
                  onClick={alternarCamera}
                  disabled={!temCamera}
                  className={`sidebar-em-chamada__ctrl ${cameraDesligada ? 'sidebar-em-chamada__ctrl--danger' : ''}`}
                  title={cameraDesligada ? 'Ligar camera' : 'Desligar camera'}
                  aria-pressed={cameraDesligada}
                  aria-label={cameraDesligada ? 'Ligar camera' : 'Desligar camera'}
                >
                  <i className={`bi ${cameraDesligada ? 'bi-camera-video-off-fill' : 'bi-camera-video-fill'}`} />
                </button>
                <button
                  type="button"
                  onClick={alternarTela}
                  className={`sidebar-em-chamada__ctrl ${compartilhandoTela ? 'sidebar-em-chamada__ctrl--ativo' : ''}`}
                  title={compartilhandoTela ? 'Parar de compartilhar' : 'Compartilhar tela'}
                  aria-pressed={compartilhandoTela}
                  aria-label={compartilhandoTela ? 'Parar de compartilhar' : 'Compartilhar tela'}
                >
                  <i className="bi bi-display-fill" />
                </button>
                <button
                  type="button"
                  onClick={sairDaSala}
                  className="sidebar-em-chamada__ctrl sidebar-em-chamada__ctrl--sair"
                  title="Sair da chamada"
                  aria-label="Sair da chamada"
                >
                  <i className="bi bi-telephone-x-fill" />
                </button>
              </div>
            </div>
          )}

          {visibleModules.map((mod) => {
            const renderItem = (item) => {
              const mostraBadge = item.to === '/ti/atendimentos' && naoLidas > 0;
              const mostraVencidos = item.to === '/ti/chamados' && vencidos > 0;
              const mostraSolicitacoes = item.to === '/ti/solicitacoes' && solicitacoesPendentes > 0;
              const mostraChecklist = item.to === '/ti/checklist' && checklistPendentes > 0;
              return (
                <NavLink key={item.to} to={item.to} onClick={handleNavigate} data-tooltip={item.label}>
                  <span className="sidebar-nav__icon-wrap">
                    <i className={`bi ${item.icon}`} />
                    {mostraBadge && (
                      <span className="sidebar-nav__badge">
                        {naoLidas > 99 ? '99+' : naoLidas}
                      </span>
                    )}
                    {mostraVencidos && (
                      <span
                        className="sidebar-nav__badge"
                        style={{ background: '#dc3545' }}
                        title="Chamados vencidos"
                      >
                        {vencidos > 99 ? '99+' : vencidos}
                      </span>
                    )}
                    {mostraSolicitacoes && (
                      <span
                        className="sidebar-nav__badge"
                        style={{ background: '#fd7e14' }}
                        title="Solicitacoes pendentes"
                      >
                        {solicitacoesPendentes > 99 ? '99+' : solicitacoesPendentes}
                      </span>
                    )}
                    {mostraChecklist && (
                      <span
                        className="sidebar-nav__badge"
                        style={{ background: '#fd7e14' }}
                        title="Checkpoints pendentes hoje"
                      >
                        {checklistPendentes > 99 ? '99+' : checklistPendentes}
                      </span>
                    )}
                  </span>
                  <span className="nav-label">{item.label}</span>
                </NavLink>
              );
            };

            // Lista achatada: grupos viram rotulos/divisorias (que somem ao
            // recolher) e os itens ficam numa lista unica — os mesmos icones
            // nos dois estados. Modulos "standalone" (Dashboard etc.) nao tem
            // rotulo.
            return (
              <div key={mod.key} className="sidebar-group">
                {!mod.standalone && (
                  <div className="sidebar-group__label">{mod.label}</div>
                )}
                {mod.items.map(renderItem)}
              </div>
            );
          })}

          <div className="sidebar-section">Operacao</div>
          <div className="sidebar-info-card">
            <strong>{currentSection?.label || 'Dashboard'}</strong>
            <div>
              {currentSection?.description || 'Acompanhe a operacao da TI: equipamentos, chamados e insumos.'}
            </div>
          </div>
        </div>

        <div className="sidebar-footer sidebar-footer--model">
          <div className="sidebar-user-block">
            <div className="sidebar-user-line">
              <i className="bi bi-person-circle" />
              <div>
                <strong>{user?.nome || 'Operador'}</strong>
                <span>{user?.usuario || 'sem identificacao'}</span>
              </div>
            </div>
          </div>

          <div className="sidebar-footer-actions">
            <button type="button" className="sidebar-footer-link" onClick={() => navigate('/ti/dashboard')}>
              <i className="bi bi-grid-1x2" />
              <span>Inicio</span>
            </button>
            <button type="button" className="sidebar-footer-link" onClick={handleLogout}>
              <i className="bi bi-box-arrow-right" />
              <span>Sair</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
